import { cloneDeep } from 'lodash';

import { api } from '../../../services/api';

import { sortAlphabetical } from '../../../utils/utils';
import { session } from '../../../services/session';
import { invalidPage, calculatePagination, checkAllSites } from '../common/utils';
import { StatCache } from './models';

export class PublisherMegatableService {
  constructor() {
    this.Api = api;
    this.Session = session;

    this.limit = 500; // The size of API responses, perPage is for the display
    this.reducedLimit = 100; // Don't need to always query 500 rows, 100 improves latency
    this.paginationOptions = [500, 100, 50];

    // Keep track of breadcrumbs throughout megatable components
    this.breadcrumbs = [];
    this.breadcrumbClicked = false;

    // Order of params for megatable queries
    this.orderedKeys = [
      'date_from',
      'date_to',
      'order_by',
      'direction',
      'edit_ids',
      'auction_id',
      'edit_id',
      'search_query',
      'networks',
      'granularity',
      'merch_ids',
      'group_by',
      'is_total',
      'page',
      'per_page',
      'no_returns',
      'additional_pubs',
      'additional_merchs',
      'pub_ids',
      'auction_ids',
    ];

    // Preserves stats of megatable components
    this.statCacheKeyedOnType = {
      article: null, // new StatCache()
      smartlink: null,
      merchant_clicks: null,
      merchant_epc: null,
      article_graph: null,
      merchantArticle: null,
      merchantRecommendation: null,
      merchantArticle_graph: null,
      merchantRecommendation_graph: null,
      merchantTopPublisher: null,
      /*
       * Graphed rows not in current page, needed for graphed IDs in
       * the query param to be honored when not in current page of stats
       */
      article_graph_rows: null,
    };
    this.merchants = [];
    this.publishers = [];

    this.reportStatTypes = {
      merchantArticle: 'merchant_articles',
      merchantRecommendation: 'merchant_recommendations',
      merchantArticleSearch: 'merchant_article_search',
      merchantProductsSearch: 'article_search_products',
      article: 'articles',
      smartlink: 'smartlinks',
      merchant: 'merchants',
      product: 'products',
    };
  }

  getStats(orgId, statType, pageNum, perPage, params, resetSearch) {
    const statCache = this.getStatCache(statType, resetSearch, true);

    const limit = statType === 'merchantTopPublisher' ? this.reducedLimit : this.limit;

    [this.page, this.indexStart, this.indexEnd] = calculatePagination(pageNum, perPage, limit);

    if (invalidPage(this.page, statCache.total_items, limit)) {
      return Promise.resolve('The page requested does not exist');
    }

    if (statCache.pages[this.page]) {
      return this.sliceMegatableData(statCache);
    }

    checkAllSites(params);
    params.page = this.page;
    params.per_page = limit;
    params = this.orderParams(params);

    return this.queryPublisherStats(statType, orgId, params).then(resp => {
      return this.sliceMegatableData(statCache);
    });
  }

  queryPublisherStats(statType, orgId, params) {
    const endpoint = this.getStatEndpoint(orgId, statType);

    return this.Api.get(endpoint, params).then(resp => {
      const data = resp.data.data[0];
      const statCache = this.statCacheKeyedOnType[statType];

      statCache.pages[this.page] = data.stats;
      statCache.total_items = data.page_info.total_items;
    });
  }

  loadStatTotals(pubId, statType, params) {
    const statCache = this.statCacheKeyedOnType[statType];
    if (statCache && statCache.totals) {
      return Promise.resolve(statCache.totals);
    }
    if (params.search_query) return Promise.resolve([]);

    const endpoint = this.getStatEndpoint(pubId, statType, true);
    params.page = 1;
    params.per_page = statType === 'merchantTopPublisher' ? this.reducedLimit : this.limit;
    checkAllSites(params);
    params = this.orderParams(params);

    return this.Api.get(endpoint, params).then(resp => {
      // New pattern on BE for Totals sends just a row-dict back, not an array with a single row-dict
      const data = resp.data.data[0].stats;
      statCache.totals = statType === 'merchantTopPublisher' ? data : data[0];
      return statCache.totals;
    });
  }

  // TODO: Deprecate after switching over overview-table to getOrgPartners()
  getMerchants() {
    if (this.merchants.length > 0) {
      return Promise.resolve(this.merchants);
    }

    return this.Api.get('/api/v0/merchants/')
      .then(resp => {
        /*
         * TODO: legacy endpoint doesn't allow easy filtering, refactor to params after
         * backend filters are implemented.
         */
        const merchants = resp.data.data
          .filter(merchant => {
            return merchant.show_in_dashboard;
          })
          .map(merchant => {
            return {
              ...merchant,
              label: merchant.full_name,
              selected: true, // All merchants selected by default
            };
          });

        this.merchants = sortAlphabetical(merchants, 'full_name');
        return this.merchants;
      })
      .catch(() => []);
  }

  getOrgPartners(orgType) {
    if (this[orgType].length > 0) {
      return Promise.resolve(this[orgType]);
    }

    return this.Api.get(`/api/v0/${orgType}/`)
      .then(resp => {
        const partners = resp.data.data
          .filter(partner => {
            return partner.show_in_dashboard;
          })
          .map(partner => {
            return { ...partner, label: partner.full_name, selected: true };
          });

        this[orgType] = sortAlphabetical(partners, 'full_name');
        return this[orgType];
      })
      .catch(() => []);
  }

  /* Helper function */
  orderParams(params) {
    const orderedParams = {};
    this.orderedKeys.forEach(key => {
      if (params[key]) {
        orderedParams[key] = params[key];
      }
    });

    return orderedParams;
  }

  getQueryParams(urlParams, oldState, statType) {
    // Translate params from query string into params for the state
    /*
     * newParams: contains lower-level params (paramKeys)
     * that have the same value in state and query string.
     * page, perPage, networks are handled on the top level bc
     * their values differ btwn state (objects) and request params (strings).
     */
    let newParams = this.getParamsFromQueryString(urlParams, statType);
    newParams = this.getParamsFromSession(newParams);

    const newNetworks = this.getNetworksFromString(
      urlParams.get('networks'),
      cloneDeep(oldState.networks),
      statType,
    );
    const newGranularities = this.getGranularitiesFromString(
      urlParams.get('granularity'),
      cloneDeep(oldState.granularities),
    );
    const newMerchants = this.getPartnersFromString(
      urlParams.get('merch_ids'),
      cloneDeep(oldState.merchants),
      'merch_id',
    );
    const newPublishers = this.getPartnersFromString(
      urlParams.get('pub_ids'),
      cloneDeep(oldState.publishers),
      'pub_id',
    );
    const page = parseInt(urlParams.get('page')) || oldState.page;
    const perPage = parseInt(urlParams.get('per_page')) || oldState.perPage;

    return [newParams, page, perPage, newNetworks, newGranularities, newMerchants, newPublishers];
  }

  getParamsFromQueryString(urlParams, statType) {
    const newParams = {};

    const paramKeys = [
      'date_from',
      'date_to',
      'order_by',
      'direction',
      'search_query',
      'granularity',
      'graphed_ids',
    ];

    if (statType === 'article') {
      paramKeys.push('edit_ids');
    } else if (statType === 'smartlink') {
      paramKeys.push('edit_id');
    } else if (statType === 'product') {
      paramKeys.push('auction_id');
    }

    paramKeys.forEach(key => {
      let value = urlParams.get(key);

      if (key === 'edit_id') value = parseInt(value);

      if (value && value !== 'null') {
        newParams[key] = value;
      } else if (!value && key === 'search_query') {
        newParams.search_query = null;
      }
    });

    return newParams;
  }

  getParamsFromSession(queryParams) {
    /*
     * If these params are not specified in the URL, we pull them from session.
     * This way they can be retained until the user signs out.
     */
    const sessionKeys = ['date_from', 'date_to'];

    sessionKeys.forEach(key => {
      const { dashboardParams } = this.Session;

      if (!queryParams[key] && dashboardParams && dashboardParams[key]) {
        queryParams[key] = dashboardParams[key];
      }
    });

    return queryParams;
  }

  getNetworksFromString(networkString, currentNetworks, statType) {
    if (!networkString) return null;

    const newNetworks = currentNetworks.map(network => {
      // Currently backend doesn't support networks for product stats query
      if (statType === 'product') {
        network.selected = false;
      } else {
        network.selected = networkString.split(',').includes(network.name);
      }
      return network;
    });

    return newNetworks;
  }

  getGranularitiesFromString(granularityString, currentGranularities) {
    if (!granularityString) return null;

    const newGranularities = currentGranularities.map(granularity => {
      granularity.selected = granularity.name === granularityString;
      return granularity;
    });

    return newGranularities;
  }

  getPartnersFromString(partnerIdString, currentPartners, idString) {
    if (!partnerIdString) return null;

    const partnerIdList = partnerIdString.split(',').map(partnerId => parseInt(partnerId));
    return currentPartners.map(partner => {
      partner.selected = partnerIdList.includes(partner[idString]);
      return partner;
    });
  }

  getNetworkString(networkObjects) {
    // Convert an array of networks to comma separated string `narrativ,rakuten`
    return networkObjects
      .filter(network => network.selected)
      .map(network => network.name)
      .join(',');
  }

  getPartnerIds(partners, idPrefix) {
    // Convert array of partners to comma separated list of pub/merch IDs
    return partners
      .filter(partner => partner.selected)
      .map(partner => partner[`${idPrefix}_id`])
      .join(',');
  }

  getRequestParams(state, statType, isTotal, partnerType) {
    // Translate the state params into params for the API request
    const params = cloneDeep(state.params);
    const merchantStatTables = ['merchantArticle', 'merchantRecommendation'];

    const networkStr = this.getNetworkString(state.networks);
    if (networkStr) params.networks = networkStr;

    const idLabel = partnerType === 'merchants' ? 'merch_ids' : 'pub_ids';
    const allPartners = state[partnerType];
    const partnerIds = this.getPartnerIds(allPartners, idLabel.split('_')[0]);
    const allPartnersSelected =
      allPartners.length === allPartners.filter(partner => partner.selected).length;

    // If all partners are selected and we're on Top-Pubs page, send no ids instead of all ids
    // TODO: Refactor all tables to utilize this for optimization
    if (partnerIds.length && !(allPartnersSelected && statType === 'merchantTopPublisher')) {
      params[idLabel] = partnerIds;
    }

    if (isTotal) {
      delete params.direction;
      delete params.order_by;

      if (statType === 'product') {
        params.is_total = true;
      } else {
        params.group_by =
          statType === 'merchantTopPublisher'
            ? null
            : merchantStatTables.includes(statType)
            ? 'merch_id'
            : 'pub_id';
      }
    }

    if (statType === 'product') {
      params.no_returns = true;
    }

    if (merchantStatTables.includes(statType)) {
      delete params.networks;
    }

    // If a param is null, remove it from the API request params
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === 'null') {
        delete params[key];
      }
    });

    return params;
  }

  getStatEndpoint(orgId, statType, isTotal) {
    const endpointMap = {
      article: `/api/v0/publishers${orgId ? `/${orgId}` : ''}/stats_by_edit/`,
      article_graph: `/api/v0/publishers/${orgId}/edit_stats_by_date/`,
      merchant: `/api/v0/publishers/${orgId}/stats_by_merchant/`,
      merchantArticle: `/api/v0/merchants/${orgId}/merch_ed/stats_by_edit/`,
      merchantRecommendation: `/api/v0/merchants/${orgId}/merch_ed/stats_by_recommendation/`,
      merchantArticle_graph: `/api/v0/merchants/${orgId}/edit_stats_by_date/`,
      merchantRecommendation_graph: `/api/v0/merchants/${orgId}/recommendation_stats_by_date/`,
      merchantTopPublisher: `/api/v0/merchants/${orgId}/${
        isTotal ? 'aggregated_' : ''
      }pubs_performance/`,
      merchant_epc: `/api/v0/publishers/${orgId}/stats_by_merchant/`,
      merchant_clicks: `/api/v0/publishers/${orgId}/stats_by_merchant/`,
      product: `/api/v0/publishers/${orgId}/stats_by_product/`,
      smartlink: `/api/v0/publishers/${orgId}/stats_by_bam_link/`,
    };
    return endpointMap[statType];
  }

  getStatCache(statType, resetSearch, isStatCache = false) {
    if (resetSearch || !this.statCacheKeyedOnType[statType]) {
      this.statCacheKeyedOnType[statType] = isStatCache ? new StatCache() : {};
    }
    return this.statCacheKeyedOnType[statType];
  }

  sliceMegatableData(stats) {
    if (!stats.pages[this.page]) return Promise.resolve({ stats: [] });

    return Promise.resolve({
      stats: stats.pages[this.page].slice(this.indexStart, this.indexEnd),
      totalItems: stats.total_items,
    });
  }

  /* Megatable Graph functions */
  queryEditStats(pubId, statType, params, resetSearch) {
    // Need to handle multiple row identifiers now that we graph more than just stories
    // edit_id: Stories tables
    // auction_id: Recommendations tables
    const identifier = statType === 'merchantRecommendation_graph' ? 'auction_id' : 'edit_id';

    const { granularity } = params;
    const rowId = params[identifier];
    const statCache = this.getStatCache(statType, resetSearch);

    if (!statCache[rowId]) {
      statCache[rowId] = {};
    }

    if (statCache[rowId][granularity]) {
      return Promise.resolve(statCache[rowId][granularity]);
    }

    const endpoint = this.getStatEndpoint(pubId, statType);
    checkAllSites(params);
    params = this.orderParams(params);

    return this.Api.get(endpoint, params).then(resp => {
      const data = resp.data.data[0].stats;
      if (!this.statCacheKeyedOnType[statType][rowId]) {
        this.statCacheKeyedOnType[statType][rowId] = {};
      }
      this.statCacheKeyedOnType[statType][rowId][params.granularity] = data;

      return Promise.resolve(data);
    });
  }

  // Query specific edits when graphed rows aren't in stats
  queryGraphedRows(pubId, statType, params, resetSearch) {
    const statCacheType = `${statType}_graph_rows`;
    const statCache = this.getStatCache(statCacheType, resetSearch);
    const endpoint = this.getStatEndpoint(pubId, statType);

    // Need to handle multiple identifiers now that we graph more than just stories
    const identifier = statType === 'merchantRecommendation' ? 'auction_id' : 'edit_id';

    const graphedRowData = [];
    const missingRowIds = [];
    const idList = params[`${identifier}s`].split(',');

    if (!resetSearch) {
      idList.forEach(editId => {
        if (statCache[editId]) {
          graphedRowData.push(statCache[editId]);
        } else {
          missingRowIds.push(editId);
        }
      });
      // Generate comma-separated string of remaining edit IDs not in stat cache
      if (!missingRowIds.length) {
        return Promise.resolve(graphedRowData);
      }
      params[`${identifier}s`] = missingRowIds.join(',');
    }

    params = this.orderParams(params);

    return this.Api.get(endpoint, params).then(resp => {
      const data = resp.data.data[0].stats;
      data.forEach(edit => {
        statCache[edit[identifier]] = edit;
        graphedRowData.push(edit);
      });
      return Promise.resolve(graphedRowData);
    });
  }

  /* Report functions */
  getStatsReportPayload(
    orgId,
    orgType,
    statType,
    columns,
    reportOptions,
    queryParams,
    reportVersion,
    reportEndpoint,
  ) {
    return this.Api.get(`/api/v0/${orgType}/${orgId}/`).then(resp => {
      const { slug } = resp.data.data[0];
      let payload = {
        date_from: queryParams.date_from,
        date_to: queryParams.date_to,
      };

      if (reportOptions.reportType === 'visible') {
        payload.page = reportOptions.currentPage;
        payload.per_page = reportOptions.perPage;
      }

      const { reportParams, ...otherParams } = queryParams;

      if (reportVersion === 'V2') {
        payload = {
          ...payload,
          is_report: true,
          report_params: {
            slug,
            report_type: statType,
            endpoint: reportEndpoint,
            columns: columns.join(','),
            ...reportParams,
          },
          ...otherParams,
        };
      } else {
        payload = {
          ...payload,
          slug,
          columns: columns.join(','),
          report_type: this.reportStatTypes[statType],
          order_by: queryParams.order_by,
          direction: queryParams.direction,
          networks: queryParams.networks,
        };

        if (queryParams.edit_id) payload.edit_id = queryParams.edit_id;
        if (queryParams.search_query) payload.search_query = queryParams.search_query;
        if (queryParams.edit_ids) payload.edit_ids = queryParams.edit_ids;
        if (queryParams.auction_id) payload.auction_id = queryParams.auction_id;
        if (statType === 'merchantArticleSearch') payload.type = queryParams.type;
        if ('is_winning' in queryParams) payload.is_winning = queryParams.is_winning;
        if (['merchantArticle', 'merchantRecommendation'].includes(statType)) {
          payload.merch_ed = true;
        }
      }
      return Promise.resolve({ orgId, payload });
    });
  }

  generateStatsReport(
    orgId,
    statType,
    columns,
    reportOptions,
    queryParams,
    reportVersion,
    reportEndpoint,
  ) {
    const merchantStatTypes = [
      'merchantArticle',
      'merchantArticleSearch',
      'merchantProductsSearch',
      'merchantRecommendation',
      'merchant-performance-overview',
      'merchantTopPublisher',
      'top-five-stories',
      'top-five-pubs',
      'strategy-tab-publishers',
      'strategy-tab-stories',
      'top-pubs',
      'top-stories',
      'strategy-tab-recommendations',
      'strategy-tab-products',
      'campaign-center-strategies-tab',
      'campaign-center-campaigns-tab',
    ];
    const orgType = merchantStatTypes.includes(statType) ? 'merchants' : 'publishers';
    const v1Endpoint = `/api/v0/${orgType}/${orgId}/partner_reports/`;
    const v2Endpoint = `/api/v0/${orgType}/${orgId}/request_partner_report/`;
    return this.getStatsReportPayload(
      orgId,
      orgType,
      statType,
      columns,
      reportOptions,
      queryParams,
      reportVersion,
      reportEndpoint,
    )
      .then(data => {
        const endpoint = reportVersion === 'V2' ? v2Endpoint : v1Endpoint;
        return this.Api.post(endpoint, data.payload);
      })
      .then(reportResp => {
        this.reportKey = reportResp.data.data[0].report_key;
        return this.Api.get(`${v1Endpoint}${this.reportKey}/`);
      })
      .then(urlResp => {
        const data = {
          report_url: urlResp.data.data[0].report_url,
          report_key: this.reportKey,
          org_type: orgType,
        };
        return Promise.resolve(data);
      });
  }

  async generateProductStatsReport(params, orgId, strategy, statType) {
    const columns = [
      'product_name',
      'product_sku',
      'product_price',
      'is_in_stock',
      'publisher_total_earnings',
      'clicks',
      'publisher_epc',
    ];

    const baseUrl = `/api/v0/publishers/${orgId}/`;
    const getUrl = `${baseUrl}partner_reports/`;
    const postUrl = `${baseUrl}request_partner_report/`;

    const getSlug = await this.Api.get(`/api/v0/merchants/${strategy.merch_id}/`);

    const slug = `${getSlug.data.data[0].slug}_${strategy.strategy_name
      .toLowerCase()
      .replace(/[^0-9a-zA-Z]+/g, '_')}`;

    const payload = {
      admin_stats: session.isAdminView,
      is_report: true,
      date_from: params.date_from,
      date_to: params.date_to,
      strategy_id: strategy.strategy_id,
      report_params: {
        report_type: statType,
        columns,
        endpoint: `${baseUrl}/strategies/${strategy.strategy_id}/products_v2/`,
        slug,
      },
    };

    if (params.is_in_stock) payload.is_in_stock = params.is_in_stock;
    if (params.direction) payload.direction = params.direction;
    if (params.order_by) payload.order_by = params.order_by;

    // Get report key to get report data
    const postResponse = await this.Api.post(postUrl, payload);
    const reportKey = postResponse.data.data[0].report_key;
    const reportData = await this.Api.get(`${getUrl}${reportKey}/`);

    const data = {
      report_url: reportData.data.data[0].report_url,
      report_key: reportKey,
      org_type: 'publishers',
    };
    return Promise.resolve(data);
  }

  checkReportReady(reportUrl, reportKey, orgId, orgType, statType) {
    const statusCheckUrl = `/api/v0/${orgType}/${orgId}/partner_reports/${reportKey}/status/`;
    return new Promise((resolve, reject) => {
      const pollUrl = window.setInterval(() => {
        this.Api.get(statusCheckUrl, null, { headers: { Range: 'bytes=0-0' } })
          .then(response => {
            if (response.status === 200) {
              window.clearInterval(pollUrl);
              window.clearTimeout(reportTimeout);
              resolve(this.triggerReportDownload(reportUrl, statType));
            }
          })
          .catch(e => {
            // Empty error handler to avoid 404 errors in console
          });
      }, 5000);

      const reportTimeout = window.setTimeout(() => {
        window.clearInterval(pollUrl);
        // TODO: invoke ModalService when implemented
        reject('Something went wrong while generating report');
      }, 60000);
    });
  }

  triggerReportDownload(reportUrl, statType) {
    const { navigator } = window;

    // If in Chrome or Safari - download via virtual link click
    const isChromeOrSafari =
      (/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) ||
      (/Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor));

    if (isChromeOrSafari) {
      const el = document.createElement('a');
      el.setAttribute('href', reportUrl);
      el.style.display = 'none';

      /*
       * Some Chromium browsers like Chrome on IOS don't support downloads,
       * check if download property is available on element.
       */
      if (el.download !== undefined) {
        el.setAttribute('download', `${this.reportKey}.tsv`);
      } else {
        window.open(reportUrl);
      }

      /*
       * Add anchor el to DOM, download property tells the browser that a click
       * should trigger a download from the href. Append to header to avoid reopening
       * the reports dropdown when download is complete
       */
      const megatableHeader =
        document.querySelector('.table-container') || document.querySelector('.table-container-v2');

      megatableHeader.appendChild(el);
      el.click();
      megatableHeader.removeChild(el);
    } else {
      window.open(reportUrl);
    }
    return Promise.resolve();
  }
}

export default new PublisherMegatableService();
